import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Icon } from 'antd'
import { useParams, useHistory } from 'react-router-dom'
import { Spinner } from '../../../helpers/commonUi'
import { Http } from '../../../helpers/axois'
import { useApp } from './hooks/useApp'
import { useToast } from '../../../helpers/commonFunction'
import { IApp } from './type'
import { CreateRelease } from './CreateRelease'
import { AppState } from '../../../stateManager/store'
import style from './styles/marketplace-apps.module.css'
import { truncate } from "lodash";
import { ReactComponent as CloseButton } from "../../../assets/svg/closeButton.svg"

// icons
import { ReactComponent as ArrowLeftIcon } from '../../../assets/svg/layer-left.svg'
import { ReactComponent as ThumbnailIcon } from '../../../assets/svg/thumbnailPlaceholder.svg'


const getShortDescription = (value: string, length: number) =>
  truncate(value, { length, separator: " " });
export const App = () => {
  const params: { appId: string } = useParams()
  const { appId } = params
  const { loading, app } = useApp(appId)
  const [customModalVisible, setCustomModalVisible] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]); // Store all images
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Initialize selectedImageIndex with 0

  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closeCustomModal();
      };
    }
    
      const handleClickOutside = (e: MouseEvent) => {
        if (e.target instanceof HTMLElement && e.target.id === "modelContent") {
          closeCustomModal();
        };
      };
    
      if (customModalVisible) {
        window.addEventListener('keydown', handleEscKey);
        window.addEventListener('click', handleClickOutside);
      }
    
      return () => {
        window.removeEventListener('keydown', handleEscKey);
        window.removeEventListener('click', handleClickOutside);
      };
  }, [customModalVisible]);


  const openCustomModal = (images: string[], initialIndex: number) => {
    setPreviewImages(images);
    setCurrentImageIndex(initialIndex);
    setCustomModalVisible(true);
  };

  const closeCustomModal = () => {
    setCustomModalVisible(false);
  };

  if (loading) {
    return <Spinner />
  }

  return (
    <div className={style.app}>
      {customModalVisible && (
        <div className={style.customModal} id="customModal">
          <div className={style.modalContent} id="modelContent">
            <div className={style.modalImageContainer}>
              <img
                src={previewImages[currentImageIndex]}
                alt="Preview screenshot"
                className={style.previewImage}
              />
            </div>
            <span className={style.closeButton} onClick={closeCustomModal}>
              <CloseButton data-test-id="close" type="close" />
            </span>
          </div>
          {app.screenshots.length && (
            <div className={style.screenShotsContainer }>
              <ImagesCarousel  screenshots={app.screenshots}  openCustomModal={openCustomModal}   selectedImageIndex={selectedImageIndex}  setSelectedImageIndex={setSelectedImageIndex}
              />           
            </div>
          )}
        </div>
      )}
      <Header app={app} />
      <div className={style.appWrapper}>
        <Card app={app} />
        <span className={style.description}>Description</span>
        <hr className={style.appRuler} />
        <p>{app.description}</p>
        <hr className={style.appRuler} />
        {Boolean(app.screenshots.length) && (
          <>
            <Screenshots
              screenshots={app.screenshots}  openCustomModal={openCustomModal} setSelectedImageIndex={setSelectedImageIndex} />          
              <hr className={style.appRuler} />
          </>
        )}

        <ReleaseInfo app={app} />
        <hr className={style.appRuler} />

        <Metadata app={app} />
      </div>
    </div>
  );
}

interface IProps {
  app: IApp
}

const Card: React.FC<IProps> = ({ app }) => {
  return (
    <div className={style.card}>
      <div
        className={style.appThumbnail}
        style={{
          boxShadow: !app.avatar.includes('png')
            ? '0px 3px 16px #95abd34b'
            : '',
        }}
      >
        {app.avatar ? (
          <img src={app.avatar} alt="App Thumbnail" />
        ) : (
          <ThumbnailIcon height={100} width={100} />
        )}
      </div>
      <div>
        <h4>{app.name}</h4>
        <p>{getShortDescription(app.description, 77)}</p>
      </div>
    </div>
  )
}

const Metadata: React.FC<IProps> = ({ app }) => {
  const { createdBy, authorWebsite } = app
  const { firstName, lastName } = createdBy
  const author = firstName + ' ' + lastName

  return (
    <div className={style.appMetadata}>
      <div className={style.metadataItem}>
        <p>Author</p>
        <p>{author}</p>
      </div>
      <div className={style.metadataItem}>
        <p>Website</p>
        <p>{authorWebsite && authorWebsite}</p>
      </div>
    </div>
  )
}

const ReleaseInfo: React.FC<IProps> = ({ app }) => {
  return (
    <div className={style.releaseInfo}>
      {app.releaseNotes && (
        <div>
          <p>What's New</p>
          <p>{app.releaseNotes}</p>
        </div>
      )}
      <div>
        <p>Version</p>
        <p>{app.currentVersion}</p>
        <p>Release Date: {app.updatedAt}</p>
      </div>
    </div>
  )
}

interface ScreenShotProps{
  screenshots: string[];
  openCustomModal: (images: string[], initialIndex: number) => void; 
  setSelectedImageIndex: (index: number) => void
}

const Screenshots: React.FC<ScreenShotProps> = ({ screenshots, openCustomModal, setSelectedImageIndex }) => {
  return (
    <div style={{ display: 'flex', gap: 32 }}>
      {screenshots.map((screenshot, idx) => (
        <div key={screenshot} className={style.screenShots}>
          <img
            src={screenshot}
            alt={`Screenshot #${idx + 1}`}
            className={style.screenshotImage}
            onClick={() => {
              openCustomModal(screenshots, idx);
              setSelectedImageIndex(idx);
            }}
          />
        </div>
      ))}
    </div>
  );
};


interface ImagesCarouselProps{
  screenshots: string[];
  openCustomModal: (images: string[], initialIndex: number) => void; 
  selectedImageIndex: number;
  setSelectedImageIndex: (index: number) => void 
}

const ImagesCarousel: React.FC<ImagesCarouselProps> = ({ screenshots, openCustomModal, selectedImageIndex, setSelectedImageIndex }) => {

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    openCustomModal(screenshots, index);
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && selectedImageIndex > 0) {
        setSelectedImageIndex(selectedImageIndex - 1);
        openCustomModal(screenshots, selectedImageIndex - 1)
      } else if (e.key === 'ArrowRight' && selectedImageIndex < screenshots.length - 1) {
        setSelectedImageIndex(selectedImageIndex + 1);
        openCustomModal(screenshots, selectedImageIndex + 1)
      }
    };
    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [selectedImageIndex, screenshots]);

  return (
    <div style={{ display: 'flex', gap: 12 }}>
      {screenshots.map((screenshot, idx) => (
        <div key={screenshot} className={style.screenShotsCarousel} style={{ boxShadow: idx === selectedImageIndex ? "0px 5px 16px #00000029" : "", border: idx === selectedImageIndex ? "2px solid #F1722E" : "none" }}>
          <img
            src={screenshot}
            alt={`Screenshot #${idx + 1}`}
            className={style.screenshotImageCaraousel}
            onClick={() => handleImageClick(idx)}
          />
        </div>
      ))}
    </div>
  );
};


const Header: React.FC<IProps> = ({ app }) => {
  const history = useHistory()
  const { ErrorToast } = useToast()

  const orgId = useSelector((state: AppState) => state.org.selectedOrg?.id)
  const userId = useSelector((state: AppState) => state.user.data.user.id)
  const { selectedOrg } = useSelector((state: AppState) => state.org)
    const loggedInUserId = useSelector((state: any) => {
        return state.user.data.user.id
    })
  const [releaseModalVisibility, setReleaseModalVisibility] = useState(false)
  const [loading, setLoading] = useState(false)
  const [installed, setInstalled] = useState(app.isInstalled)
  const orgPermissions: any = useSelector((state: any) => {
    return state.org.orgPermissions
}, (oldPermissions: any, newPermissions: any) => JSON.stringify(oldPermissions) === JSON.stringify(newPermissions))

  const handleInstall = async () => {
    if ((!orgPermissions?.includes("marketplace.PublishApp") || !orgPermissions.includes('org.View'))&& loggedInUserId!=selectedOrg?.owner ) {
      ErrorToast("Not Authorized: Please request owner of this organization to provide access")
      return
    }
    if (installed) {
      return
    }

    const endpoint = `/orgs/${orgId}/marketplaceApps/install/${app.id}`
    setLoading(true)
    try {
      await Http.post(endpoint).toPromise()
      setInstalled(true)
    } catch (err) {
      var msg = 'Failed to install app'
      if (err === "Forbidden") {
        msg = "Not Authorized: Please request owner of this organization to provide access"
      }
      ErrorToast(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={style.appHeader}>
      <p>
        <ArrowLeftIcon className="mr-2" onClick={() => history.push('/marketplace/apps')} />
        {app.name}
      </p>

      <div style={{ display: 'flex', gap: 16 }}>
        {app.createdBy.id === userId && (
          <Button
            onClick={() => setReleaseModalVisibility(true)}
            type="primary"
          >
            Create Release
          </Button>
        )}
        <Button onClick={handleInstall} loading={loading}>
          {installed ? 'Installed' : 'Install'}
        </Button>
      </div>

      <CreateRelease
        visible={releaseModalVisibility}
        setVisible={setReleaseModalVisibility}
        appId={app.id}
        currentVersion={app.currentVersion}
      />
    </div>
  )
}
